/*!
 *                                                                                                                         (℠)
 *  # sML.js | I'm a Simple and Middling Library.
 *
 *  * © Satoru Matsushima - https://github.com/satorumurmur/sML
 *  * Open source under the MIT license. - https://github.com/satorumurmur/sML/blob/master/LICENSE
 *
 *  Extracted from: https://github.com/satorumurmur/sML/blob/e41371faeeb7df30de35a378c5378fc90c033f10/sML.js
 *  Only using the browser/os identification parts.
 */

// we just want typing from readium/navigator
import type { sML } from "@readium/navigator";

interface UAData {
  brands: { brand: string; version: number }[];
}

const NUAD =
    "userAgentData" in navigator
      ? (navigator.userAgentData as UAData)
      : undefined,
  NUA = navigator.userAgent;

const _sV = (V: string | number | undefined = undefined): number[] =>
  (typeof V == "string" || typeof V == "number") && V
    ? String(V)
        .replace(/_/g, ".")
        .split(".")
        .map((I) => parseInt(I) || 0)
    : [];
const _dV = (Pre: string | undefined = undefined) => {
  if (!Pre) return [];
  const RE = new RegExp("^.*" + Pre + "[ :\\/]?(\\d+([\\._]\\d+)*).*$");
  if (!RE.test(NUA)) return [];
  return _sV(NUA.replace(RE, "$1"));
};

const OperatingSystem = ((OS: Record<string, unknown>) => {
  if (/(macOS|Mac OS X)/.test(NUA)) {
    if (/\(iP(hone|od touch);/.test(NUA)) OS.iOS = _dV("CPU (?:iPhone )?OS ");
    if (/\(iPad;/.test(NUA)) OS.iOS = OS.iPadOS = _dV("CPU (?:iPhone )?OS ");
    else if (/(macOS|Mac OS X) \d/.test(NUA))
      document.ontouchend !== undefined
        ? (OS.iOS = OS.iPadOS = _dV())
        : (OS.macOS = _dV("(?:macOS|Mac OS X) "));
  } else if (/Windows( NT)? \d/.test(NUA))
    OS.Windows = ((V) =>
      V[0] != 6 || !V[1] ? V : V[1] == 1 ? [7] : V[1] == 2 ? [8] : [8, 1])(
      _dV("Windows(?: NT)?")
    );
  else if (/Android \d/.test(NUA)) OS.Android = _dV("Android");
  else if (/CrOS/.test(NUA)) OS.ChromeOS = _dV();
  else if (/X11;/.test(NUA)) OS.Linux = _dV();
  return OS;
})({});

const UserAgent = ((UA: Record<string, unknown>) => {
  let _OK = false;
  if (NUAD && Array.isArray(NUAD.brands)) {
    const BnV = NUAD.brands.reduce((BnV, _) => {
      BnV[_.brand] = [_.version * 1];
      return BnV;
    }, {});
    if (BnV["Google Chrome"])
      (_OK = true),
        (UA.Blink = UA.Chromium = BnV["Chromium"] || []),
        (UA.Chrome = BnV["Google Chrome"]);
    else if (BnV["Microsoft Edge"])
      (_OK = true),
        (UA.Blink = UA.Chromium = BnV["Chromium"] || []),
        (UA.Edge = BnV["Microsoft Edge"]);
    else if (BnV["Opera"])
      (_OK = true),
        (UA.Blink = UA.Chromium = BnV["Chromium"] || []),
        (UA.Opera = BnV["Opera"]);
  }
  if (!_OK) {
    if (/ Gecko\/\d/.test(NUA)) {
      UA.Gecko = _dV("rv");
      if (/ Waterfox\/\d/.test(NUA)) UA.Waterfox = _dV("Waterfox");
      else if (/ Firefox\/\d/.test(NUA)) UA.Firefox = _dV("Firefox");
    } else if (/ Edge\/\d/.test(NUA)) {
      UA.EdgeHTML = _dV("Edge");
      UA.Edge = UA.EdgeHTML;
    } else if (/ Chrom(ium|e)\/\d/.test(NUA)) {
      UA.Blink = UA.Chromium = ((V) => (V[0] ? V : _dV("Chrome")))(
        _dV("Chromium")
      );
      if (/ EdgA?\/\d/.test(NUA))
        UA.Edge = ((V) => (V[0] ? V : _dV("Edg")))(_dV("EdgA"));
      else if (/ OPR\/\d/.test(NUA)) UA.Opera = _dV("OPR");
      else if (/ Vivaldi\/\d/.test(NUA)) UA.Vivaldi = _dV("Vivaldi");
      else if (/ Silk\/\d/.test(NUA)) UA.Silk = _dV("Silk");
      else if (/ UCBrowser\/\d/.test(NUA)) UA.UCBrowser = _dV("UCBrowser");
      else if (/ Phoebe\/\d/.test(NUA)) UA.Phoebe = _dV("Phoebe");
      else UA.Chrome = ((V) => (V[0] ? V : UA.Chromium))(_dV("Chrome"));
    } else if (/ AppleWebKit\/\d/.test(NUA)) {
      UA.WebKit = _dV("AppleWebKit");
      if (/ CriOS \d/.test(NUA)) UA.Chrome = _dV("CriOS");
      else if (/ FxiOS \d/.test(NUA)) UA.Firefox = _dV("FxiOS");
      else if (/ EdgiOS\/\d/.test(NUA)) UA.Edge = _dV("EdgiOS");
      else if (/ Version\/\d/.test(NUA)) UA.Safari = _dV("Version");
    } else if (/ Trident\/\d/.test(NUA)) {
      UA.Trident = _dV("Trident");
      UA.InternetExplorer = ((V) => (V[0] ? V : _dV("MSIE")))(_dV("rv"));
    }
  }
  /*+*/ if (/[\[; ]FB(AN|_IAB)\//.test(NUA)) UA.Facebook = _dV("FBAV");
  /*+*/ if (/ Line\/\d/.test(NUA)) UA.LINE = _dV("Line");
  return UA;
})({});

export const OS = OperatingSystem as unknown as (typeof sML)["OS"];
export const UA = UserAgent as unknown as (typeof sML)["UA"];
